export interface TamDayBucket {
  date: string;
  count: number;
}

export interface TamWeekBucket {
  id: string;
  label: string;
  days: TamDayBucket[];
  count: number;
}

export interface TamMonthBucket {
  monthKey: string;
  label: string;
  weeks: TamWeekBucket[];
  count: number;
}

export interface TamYearSummary {
  year: number;
  total: number;
  months: TamMonthBucket[];
}

function weekStartKey(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  const dow = d.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function formatMonthLabel(year: number, month: number): string {
  const d = new Date(year, month - 1, 1);
  return d.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
}

function formatWeekLabel(weekStart: string): string {
  const start = new Date(`${weekStart}T12:00:00`);
  const end = new Date(`${weekStart}T12:00:00`);
  end.setDate(end.getDate() + 6);
  const fmt = (dt: Date) =>
    dt.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  return `${fmt(start)} – ${fmt(end)}`;
}

function formatDayLabel(isoDate: string): string {
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString('es-MX', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
}

export function formatDayLabelShort(isoDate: string): string {
  return formatDayLabel(isoDate);
}

export function buildTamYearSummaryFromCounts(
  dailyCounts: Map<string, number>,
  year: number,
  fromDate: string,
  toDate: string,
): TamYearSummary {
  const now = new Date();
  const lastMonth = year === now.getFullYear() ? now.getMonth() + 1 : 12;
  const months: TamMonthBucket[] = [];
  let total = 0;

  for (let m = 1; m <= lastMonth; m++) {
    const monthKey = `${year}-${String(m).padStart(2, '0')}`;
    const daysInMonth = new Date(year, m, 0).getDate();
    const weekMap = new Map<string, TamDayBucket[]>();
    let monthTotal = 0;

    for (let d = 1; d <= daysInMonth; d++) {
      const date = `${monthKey}-${String(d).padStart(2, '0')}`;
      if (date < fromDate || date > toDate) continue;
      const count = dailyCounts.get(date) ?? 0;
      if (count === 0) continue;
      monthTotal += count;
      const weekId = weekStartKey(date);
      const list = weekMap.get(weekId) ?? [];
      list.push({ date, count });
      weekMap.set(weekId, list);
    }

    total += monthTotal;

    const weeks = Array.from(weekMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([weekId, days]) => ({
        id: weekId,
        label: formatWeekLabel(weekId),
        days: days.sort((a, b) => a.date.localeCompare(b.date)),
        count: days.reduce((sum, day) => sum + day.count, 0),
      }));

    months.push({
      monthKey,
      label: formatMonthLabel(year, m),
      weeks,
      count: monthTotal,
    });
  }

  return { year, total, months };
}

export function currentYear(): number {
  return new Date().getFullYear();
}

export function yearDateRange(year: number): { fromDate: string; toDate: string } {
  const now = new Date();
  const toDate =
    year === now.getFullYear()
      ? now.toISOString().slice(0, 10)
      : `${year}-12-31`;
  return { fromDate: `${year}-01-01`, toDate };
}
